/**
 * dispositionAIAutoMap.js
 * ──────────────────────────────────────────────────────────
 * AI auto-mapper for the post-call disposition lightbox.
 *
 * Two entry points:
 *   - `parseDispositionNotes(notes)`     — sync heuristic (instant, no network)
 *   - `parseDispositionNotesAI(notes, opts)` — async LLM-first via
 *     /api/ai/disposition-parse, with sync heuristic as graceful fallback.
 *
 * Both return the same shape:
 *   { summary, fieldUpdates, leadUpdates, appliedKeys, raw, source? }
 */

import axios from "axios";

/* ── Async LLM-first parser ─────────────────────────────────
 * Call site signature:
 *   await parseDispositionNotesAI(notes, { apiBase, token })
 * On any failure (network / 4xx / 5xx / timeout) it silently
 * falls back to the local heuristic so the rep is never blocked.
 */
export async function parseDispositionNotesAI(notes, { apiBase, token, signal, transcript } = {}) {
  const text = String(notes || "");
  const t = String(transcript || "").trim();
  // Iter 192m — accept an optional RingCentral Whisper transcript; the
  // backend AI prompt blends transcript + notes so we get stage/substatus/
  // pain-points populated even when the rep barely typed anything.
  if (text.trim().length < 12 && t.length < 12) {
    return parseDispositionNotes(text);
  }
  if (!apiBase) return { ...parseDispositionNotes(text + (t ? "\n" + t : "")), source: "heuristic" };
  try {
    const res = await axios.post(
      `${apiBase}/ai/disposition-parse`,
      { notes: text, transcript: t || undefined },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 20000,
        signal,
      }
    );
    const d = res.data || {};
    return {
      summary: d.summary || text,
      fieldUpdates: d.fieldUpdates || {},
      leadUpdates: d.leadUpdates || {},
      appliedKeys: Array.isArray(d.appliedKeys) ? d.appliedKeys : [],
      raw: text,
      source: d.source || "llm",
    };
  } catch (_e) {
    return { ...parseDispositionNotes(text + (t ? "\n" + t : "")), source: "heuristic" };
  }
}

// Iter 192m — v2 pipeline. Old STAGE_HINTS returned stages like
// "qualified" / "negotiation" / "contract" that no longer exist. The
// heuristic now maps directly to the canonical v2 stage + substatus
// values from pipelineStages.js so the dropdown picks them up cleanly.

const STAGE_HINTS = [
  // Most-specific first; first regex to match wins.
  { rx: /closed lost|not moving forward|declined|going with (?:another|competitor)/i, stage: "closed_lost", sub: "Chose Competitor" },
  { rx: /(signed|deal won|closed (?:won|the deal)|contract signed)/i,                 stage: "closed_won",  sub: "" },
  { rx: /(implement|go.?live|onboarding|training scheduled)/i,                        stage: "implementation", sub: "Implementation — Setup" },
  { rx: /contract sent|paperwork sent/i,                                              stage: "proposal",    sub: "Contract Sent" },
  { rx: /proposal (sent|delivered)|quote sent|pricing sent/i,                         stage: "proposal",    sub: "Quote Sent" },
  { rx: /verbal (commit|yes|agreement)/i,                                             stage: "proposal",    sub: "Verbal Commitment" },
  { rx: /(negotiat|pricing pushback|counter)/i,                                       stage: "decision",    sub: "Negotiation" },
  { rx: /(on hold|hold off|paused)/i,                                                 stage: "decision",    sub: "On Hold" },
  { rx: /escalat/i,                                                                    stage: "decision",    sub: "Escalated to Leadership" },
  { rx: /demo no.?show/i,                                                              stage: "demo",        sub: "Demo No-Show" },
  { rx: /demo (completed|done|finished)/i,                                             stage: "demo",        sub: "Demo Completed" },
  { rx: /demo (rescheduled|moved|pushed)/i,                                            stage: "demo",        sub: "Demo Rescheduled" },
  { rx: /demo (booked|scheduled|set)/i,                                                stage: "demo",        sub: "Demo Scheduled" },
  { rx: /sent (follow|materials|case study|deck)/i,                                    stage: "post_demo",   sub: "Sent Follow-Up Materials" },
  { rx: /ghost|no response|no reply/i,                                                 stage: "post_demo",   sub: "Ghosting / No Response" },
  { rx: /awaiting (internal|decision|board)/i,                                         stage: "post_demo",   sub: "Awaiting Internal Decision" },
  // Iter 195 (Feb 2026) — Voicemail discrimination. The old regex matched
  // ANY mention of "voicemail" — including the VM greeting captured in a
  // Whisper transcript ("leave a message after the tone", "you've reached
  // …"). Tighten to require an explicit rep action ("I left a vm", "vm
  // dropped", "left message"), and explicitly skip greeting phrases.
  { rx: /(?:^|\b)(?:i\s+)?(?:left|dropped|leaving)\s+(?:a\s+|the\s+)?(?:voicemail|vm|message)\b|\bvm\s+(?:left|dropped|sent)\b|\bvoicemail\s+(?:left|dropped|sent)\b/i,
    stage: "contacted", sub: "Left Voicemail",
    // Negative guard: skip if the only "voicemail" mention is the greeting
    // text that Whisper captured from the recording.
    not: /(?:leave (?:a |your )?(?:message|name|number) (?:after|at) the (?:tone|beep)|you(?:'ve| have) reached|please leave your|after the (?:tone|beep)|at the sound of the)/i,
  },
  { rx: /(?:no\s+answer|rang\s+out|went\s+to\s+voicemail|reached\s+voicemail|got\s+(?:their\s+)?voicemail|hit\s+voicemail|hung\s+up\s+on\s+(?:the\s+)?(?:voicemail|vm|greeting))(?!.*(?:left|dropped|leaving)\s+(?:a\s+|the\s+)?(?:voicemail|vm|message))/i,
    stage: "contacted", sub: "Needs Follow-Up" },
  { rx: /spoke (?:with|to) (?:the )?(?:office manager|practice manager)/i,             stage: "contacted",   sub: "Contacted — Office Manager" },
  { rx: /spoke (?:with|to) (?:the )?(?:doctor|physician|md|dr\b)/i,                    stage: "contacted",   sub: "Contacted — Physician / Doctor (MD/DO)" },
  { rx: /spoke (?:with|to) (?:the )?(?:owner|partner)/i,                               stage: "contacted",   sub: "Contacted — Physician Owner / Partner" },
  { rx: /spoke (?:with|to) (?:the )?(?:front desk|receptionist)/i,                     stage: "contacted",   sub: "Contacted — Front Desk / Receptionist" },
  { rx: /needs follow|callback requested/i,                                            stage: "contacted",   sub: "Needs Follow-Up" },
];

const NEXT_STEP_HINTS = [
  { rx: /send.*(proposal|quote|pricing)/i,           value: "Send proposal" },
  { rx: /(book|schedule).*(demo|walkthrough)/i,      value: "Schedule demo" },
  { rx: /send.*(case study|whitepaper|deck)/i,       value: "Send case study" },
  { rx: /loop in.*(tech|engineer|implementation)/i,  value: "Loop in technical resource" },
  { rx: /(re-?engage|circle back).*30/i,             value: "Re-engage in 30 days" },
  { rx: /wait.*response/i,                           value: "Wait for response" },
];

const SENTIMENT_HINTS = [
  { rx: /(love|excited|enthusias|amaz|great fit|perfect)/i, value: "very_positive" },
  { rx: /(interested|positive|engag|open to)/i,             value: "positive" },
  { rx: /(skeptic|hesitant|concerned|push back)/i,          value: "negative" },
  { rx: /(angry|frustrat|not interested|hostile|hung up)/i, value: "very_negative" },
];

const PAIN_POINT_HINTS = [
  { rx: /billing|denial|claim|rcm/i,                                  value: "Billing & RCM" },
  { rx: /(scheduling|booking|no.?show)/i,                             value: "Scheduling" },
  { rx: /(prior auth|pre.?auth)/i,                                    value: "Prior Authorization" },
  { rx: /(documentation|charting|notes)/i,                            value: "Documentation" },
  { rx: /(patient engagement|portal|comms)/i,                         value: "Patient Engagement" },
  { rx: /(reporting|mips|hedis|quality)/i,                            value: "Reporting & Quality" },
  { rx: /(staff|burnout|turnover|hiring)/i,                           value: "Staffing" },
];

const PRODUCT_HINTS = [
  { rx: /\bccm\b|chronic care/i,                  value: "CCM" },
  { rx: /\brpm\b|remote.*monitor/i,               value: "RPM" },
  { rx: /\bapcm\b/i,                              value: "APCM" },
  { rx: /\bawv\b|annual wellness/i,               value: "AWV" },
  { rx: /\behr\b|electronic.*record/i,            value: "EHR" },
  { rx: /\brcm\b|revenue cycle/i,                 value: "RCM" },
  { rx: /\bpm\b|practice management/i,            value: "PM" },
];

const TIMELINE_HINTS = [
  { rx: /(this (week|month)|asap|immediately|urgent)/i, value: "0-30 days" },
  { rx: /(next month|30 days)/i,                       value: "30-60 days" },
  { rx: /(60 days|q[1-4]|next quarter)/i,              value: "60-90 days" },
  { rx: /(this year|by year.?end)/i,                   value: "3-6 months" },
  { rx: /(next year|long.?term|future)/i,              value: "6+ months" },
];

function nextDate(daysFromNow) {
  const d = new Date(Date.now() + daysFromNow * 86400000);
  return d.toISOString().slice(0, 10);
}
function nextWeekday(dayIdx) {
  // dayIdx: 0=Sun, 1=Mon, … 6=Sat
  const d = new Date();
  const offset = ((dayIdx - d.getDay()) + 7) % 7 || 7;
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}
function parseFollowUpDate(text) {
  const t = text.toLowerCase();
  if (/tomorrow/.test(t)) return nextDate(1);
  if (/next week/.test(t)) return nextDate(7);
  if (/in (\d+) days?/.test(t)) {
    const m = t.match(/in (\d+) days?/);
    return nextDate(Math.min(60, parseInt(m[1], 10)));
  }
  if (/monday/.test(t))    return nextWeekday(1);
  if (/tuesday/.test(t))   return nextWeekday(2);
  if (/wednesday/.test(t)) return nextWeekday(3);
  if (/thursday/.test(t))  return nextWeekday(4);
  if (/friday/.test(t))    return nextWeekday(5);
  return null;
}

/* Generate a structured summary from raw notes */
function buildSummary(notes, extracted) {
  if (!notes || !notes.trim()) return "";
  const bullets = [];
  if (extracted.sentiment)            bullets.push(`Sentiment: ${extracted.sentiment.replace("_", " ")}`);
  if (extracted.stage)                bullets.push(`Stage suggestion: ${extracted.stage}${extracted.subStatus ? ` — ${extracted.subStatus}` : ""}`);
  if (extracted.decisionMakerReached) bullets.push("Decision-maker reached");
  if (extracted.budgetConfirmed)      bullets.push("Budget confirmed");
  if (extracted.painPoints?.length)   bullets.push(`Top pain points: ${extracted.painPoints.join(", ")}`);
  if (extracted.productsOfInterest?.length) bullets.push(`Products discussed: ${extracted.productsOfInterest.join(", ")}`);
  if (extracted.timeline)             bullets.push(`Prospect timeline: ${extracted.timeline}`);
  if (extracted.nextStep)             bullets.push(`Next step: ${extracted.nextStep}${extracted.followupDate ? ` (${extracted.followupDate})` : ""}`);

  const header = bullets.length
    ? `✦ AI summary\n${bullets.map((b) => `• ${b}`).join("\n")}\n\n`
    : "";
  return `${header}— Rep notes —\n${notes.trim()}`;
}

/**
 * Main entry point — parse free-form notes and emit structured updates.
 * @param {string} notes raw rep notes
 * @returns {{summary, fieldUpdates, leadUpdates, appliedKeys, raw}}
 */
export function parseDispositionNotes(notes) {
  const text = String(notes || "");
  if (text.trim().length < 12) {
    return { summary: text, fieldUpdates: {}, leadUpdates: {}, appliedKeys: [], raw: text };
  }

  const fieldUpdates = {};
  const leadUpdates = {};
  const appliedKeys = [];

  // Sentiment
  for (const h of SENTIMENT_HINTS) if (h.rx.test(text)) { fieldUpdates.sentiment = h.value; appliedKeys.push("sentiment"); break; }
  // Stage — Iter 195: honor optional `not` negative-guard regex.
  for (const h of STAGE_HINTS) {
    if (!h.rx.test(text)) continue;
    if (h.not && h.not.test(text)) continue;
    fieldUpdates.stage = h.stage;
    fieldUpdates.substatus = h.sub;
    appliedKeys.push("stage", "substatus");
    break;
  }
  // Next step
  for (const h of NEXT_STEP_HINTS) if (h.rx.test(text)) { fieldUpdates.nextStep = h.value; appliedKeys.push("nextStep"); break; }
  // Decision-maker reached
  if (/(spoke|talked|met) (?:with|to) (?:dr|doctor|owner|md)/i.test(text) || /decision.?maker (?:reached|talked|met)/i.test(text)) {
    fieldUpdates.decisionMakerReached = true; appliedKeys.push("decisionMakerReached");
  }
  // Budget
  if (/(budget (?:confirmed|approved|in place|allocated))|approved.*budget|funding (?:secured|approved)/i.test(text)) {
    fieldUpdates.budgetConfirmed = true; appliedKeys.push("budgetConfirmed");
  }
  // Follow-up date
  const fuDate = parseFollowUpDate(text);
  if (fuDate) { fieldUpdates.followupDate = fuDate; fieldUpdates.followup = `${fuDate}T10:00`; appliedKeys.push("followup"); }

  // ── Lead-info inferences ──
  const painPoints = [];
  for (const h of PAIN_POINT_HINTS) if (h.rx.test(text) && !painPoints.includes(h.value)) painPoints.push(h.value);
  if (painPoints.length) { leadUpdates.pain_point_category = painPoints[0]; leadUpdates.pain_points = painPoints.join("; "); appliedKeys.push("pain_point_category"); }

  const products = [];
  for (const h of PRODUCT_HINTS) if (h.rx.test(text) && !products.includes(h.value)) products.push(h.value);
  if (products.length) { leadUpdates.products_of_interest = products.join("; "); appliedKeys.push("products_of_interest"); }

  for (const h of TIMELINE_HINTS) if (h.rx.test(text)) { leadUpdates.prospect_decision_timeline = h.value; appliedKeys.push("prospect_decision_timeline"); break; }

  // Lead rating from sentiment
  if (fieldUpdates.sentiment === "very_positive" || fieldUpdates.sentiment === "positive") { leadUpdates.lead_rating = "Hot"; appliedKeys.push("lead_rating"); }
  else if (fieldUpdates.sentiment === "very_negative") { leadUpdates.lead_rating = "Cold"; appliedKeys.push("lead_rating"); }

  // Summary
  const summary = buildSummary(text, {
    sentiment: fieldUpdates.sentiment,
    stage: fieldUpdates.stage,
    subStatus: fieldUpdates.substatus,
    decisionMakerReached: fieldUpdates.decisionMakerReached,
    budgetConfirmed: fieldUpdates.budgetConfirmed,
    painPoints, productsOfInterest: products, timeline: leadUpdates.prospect_decision_timeline,
    nextStep: fieldUpdates.nextStep, followupDate: fieldUpdates.followupDate,
  });

  return { summary, fieldUpdates, leadUpdates, appliedKeys, raw: text };
}

/* Human-friendly labels for the "AI applied:" toast / inline list */
export const APPLIED_LABELS = {
  sentiment: "Sentiment",
  stage: "Stage",
  substatus: "Sub-status",
  nextStep: "Next step",
  followup: "Follow-up date",
  decisionMakerReached: "Decision-maker reached",
  budgetConfirmed: "Budget confirmed",
  pain_point_category: "Pain point",
  products_of_interest: "Products discussed",
  prospect_decision_timeline: "Prospect timeline",
  lead_rating: "Lead rating",
};
